import type { FollowUpRow } from '@/lib/api-client'
import { cn, formatCurrency } from '@/lib/utils'
import { FOLLOW_UP_EXPIRY_DAYS, QUEUE_META, STATUS_META, isAutoSource } from './queue-meta'

function daysAgo(date: string | null): string | null {
  if (!date) return null
  const diff = Math.round((Date.now() - new Date(date).getTime()) / 86_400_000)
  if (diff <= 0) return 'today'
  if (diff === 1) return '1 day ago'
  return `${diff} days ago`
}

function fmtDate(date: string | null): string {
  if (!date) return '-'
  return new Date(date).toLocaleDateString('en-MY', {
    day: '2-digit',
    month: 'short',
    timeZone: 'Asia/Kuala_Lumpur',
  })
}

const MS_PER_DAY = 86_400_000

/**
 * How long the row has been open and how many days remain before the nightly
 * reconciler expires it (weekStart + FOLLOW_UP_EXPIRY_DAYS). Only meaningful
 * for unresolved rows.
 */
function openAge(row: FollowUpRow): { daysOpen: number; daysLeft: number } {
  const start = new Date(row.weekStart).getTime()
  const daysOpen = Math.max(0, Math.floor((Date.now() - start) / MS_PER_DAY))
  return { daysOpen, daysLeft: FOLLOW_UP_EXPIRY_DAYS - daysOpen }
}

function OpenForCell({ row }: { row: FollowUpRow }) {
  if (row.resolvedAt) return <span className="text-text-muted">-</span>
  const { daysOpen, daysLeft } = openAge(row)
  const urgent = daysLeft <= 7
  return (
    <>
      <div className={cn('font-semibold', urgent && 'text-destructive')}>
        {daysOpen === 0 ? 'New today' : `${daysOpen}d open`}
      </div>
      <div className={cn('text-xs', urgent ? 'font-semibold text-destructive' : 'text-text-muted')}>
        {daysLeft <= 0 ? 'expires tonight' : `expires in ${daysLeft}d`}
      </div>
    </>
  )
}

function maskPhone(phone: string | null): string {
  if (!phone) return '-'
  // 0123456789 -> 012-3xx xxxx style mask, matches mockup's partial-hide convention.
  const digits = phone.replace(/\D/g, '')
  if (digits.length < 7) return phone
  return `${digits.slice(0, 3)}-${digits.slice(3, 4)}xx ${digits.slice(-4)}`
}

/** What the Date column's date is, per queue (matches the API's EVENT_DATE). */
function eventLabel(queue: FollowUpRow['queue']): string {
  if (queue === 'consult_no_purchase') return 'consult'
  if (queue === 'no_show') return 'missed appt'
  return 'last visit'
}

interface FollowUpTableProps {
  rows: FollowUpRow[]
  isPending: boolean
  selectedId: number | null
  onSelect: (row: FollowUpRow) => void
  onWhatsApp: (row: FollowUpRow) => void
}

export function FollowUpTable({
  rows,
  isPending,
  selectedId,
  onSelect,
  onWhatsApp,
}: FollowUpTableProps) {
  if (isPending) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center text-text-muted">
        Loading follow-ups...
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center text-text-muted">
        No follow-ups match the current filters.
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[940px] text-sm">
          <thead className="bg-background/50 border-b border-border">
            <tr className="text-left text-[11px] font-bold uppercase tracking-wide text-text-muted">
              <th className="px-3 py-2.5">Patient</th>
              <th className="px-3 py-2.5">Why follow up</th>
              <th className="px-3 py-2.5">Package</th>
              <th className="px-3 py-2.5 text-right">Value</th>
              <th className="px-3 py-2.5">Date</th>
              <th className="px-3 py-2.5">Open for</th>
              <th className="px-3 py-2.5">CA</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const queueMeta = QUEUE_META[row.queue]
              const statusMeta = STATUS_META[row.status]
              const auto = row.lastEvent ? isAutoSource(row.lastEvent.source) : false
              const canWhatsApp =
                row.status === 'pending' &&
                !row.patient.blacklistedAt &&
                (row.waLink !== null || row.wa !== null)
              return (
                <tr
                  key={row.id}
                  // biome-ignore lint/a11y/useSemanticElements: table row can't be a <button> without breaking table semantics; keyboard support added via onKeyDown below.
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect(row)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onSelect(row)
                    }
                  }}
                  className={cn(
                    'cursor-pointer border-b border-border-light last:border-0 align-top',
                    selectedId === row.id ? 'bg-active' : 'hover:bg-hover'
                  )}
                >
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1.5 font-semibold text-text-dark">
                      {row.patient.name}
                      {row.patient.blacklistedAt && (
                        <span className="rounded bg-destructive/10 px-1.5 py-px text-[10px] font-extrabold uppercase tracking-wide text-destructive">
                          Blacklisted
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-xs text-text-muted">
                      {row.patient.pno} · {maskPhone(row.patient.phone)}
                    </div>
                  </td>
                  <td className="px-3 py-3 max-w-[260px]">
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-bold',
                        queueMeta.badgeClassName
                      )}
                    >
                      {queueMeta.label}
                    </span>
                    <div className="mt-1 text-xs text-text-muted">{row.reason}</div>
                  </td>
                  <td className="px-3 py-3 text-xs">
                    {row.triggerRef.packageName ?? <span className="text-text-muted">-</span>}
                    {row.triggerRef.doctor && (
                      <div className="font-mono text-text-muted">{row.triggerRef.doctor}</div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right font-bold whitespace-nowrap">
                    {row.queue === 'no_show' ? (
                      <span className="text-text-light">—</span>
                    ) : (
                      <>
                        {formatCurrency(row.value)}
                        {row.queue === 'consult_no_purchase' && (
                          <div className="text-[11px] font-normal text-text-muted">consult fee</div>
                        )}
                      </>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {fmtDate(row.eventDate)}
                    <div className="text-xs text-text-muted">
                      {eventLabel(row.queue)} · {daysAgo(row.eventDate) ?? '-'}
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <OpenForCell row={row} />
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {row.assigneeName ?? <span className="text-text-muted">Unassigned</span>}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-[12.5px]">
                      <span className={cn('h-2 w-2 rounded-full', statusMeta.dotClassName)} />
                      {statusMeta.label}
                      {auto && (
                        <span className="ml-0.5 rounded bg-success/10 px-1 py-px text-[10px] font-extrabold tracking-wide text-success">
                          AUTO
                        </span>
                      )}
                    </span>
                    {row.lastEvent?.note && (
                      <div className="mt-0.5 max-w-[160px] truncate text-xs text-text-muted">
                        &ldquo;{row.lastEvent.note}&rdquo;
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {canWhatsApp ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          onWhatsApp(row)
                        }}
                        className="rounded-lg border border-[#25d366] bg-[#25d366]/90 px-2.5 py-1 text-[12px] font-semibold text-[#062b13] hover:bg-[#25d366]"
                      >
                        WhatsApp
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          onSelect(row)
                        }}
                        className="rounded-lg border border-border bg-card px-2.5 py-1 text-[12px] font-semibold hover:bg-hover"
                      >
                        View
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
